import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, ArrowBigUp, MessagesSquare, Clock } from 'lucide-react';
import forumService, { ForumTopic } from '../../services/forumService';
import communityService from '../../services/communityService';

const ACCENT = '#3fced1';

/**
 * Wandelt Forum-Inhalt in Klartext um. Inhalte werden teils als Quill-Delta-JSON
 * gespeichert (z. B. `[{"insert":"..."}]`). contentPlain ist nicht immer gesetzt,
 * daher parsen wir das Delta defensiv und extrahieren die insert-Texte.
 */
const toPlainText = (raw?: string | null): string => {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      const ops = Array.isArray(parsed)
        ? parsed
        : Array.isArray((parsed as any)?.ops)
        ? (parsed as any).ops
        : null;
      if (ops) {
        return ops
          .map((op: any) => (typeof op?.insert === 'string' ? op.insert : ''))
          .join('')
          .replace(/\s+/g, ' ')
          .trim();
      }
    } catch {
      // kein gültiges JSON -> Rohtext verwenden
    }
  }
  return trimmed;
};

/**
 * "Neue Forum-Beiträge" – die neuesten Themen aus dem Forum.
 * Port der Flutter new_forum_topics_section.
 */
const ForumTopicsSection: React.FC = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState<ForumTopic[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    forumService
      .getTopics({ sort: 'newest', limit: 6 })
      .then((res) => {
        if (!cancelled) setTopics(Array.isArray(res.topics) ? res.topics : []);
      })
      .catch(() => {
        if (!cancelled) setTopics([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Sektion ausblenden, wenn keine Themen vorhanden sind
  if (!loading && topics.length === 0) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h2 className="section-heading flex items-center gap-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">
            <MessagesSquare size={26} style={{ color: ACCENT }} />
            Neue Forum-Beiträge
          </h2>
          <p className="mt-1.5 text-sm text-gray-400">Die aktuellsten Themen aus der Community</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/forum')}
          className="shrink-0 rounded-full border border-zinc-700 px-4 py-1.5 text-sm font-semibold text-gray-300 transition-colors hover:border-[#3fced1]/60 hover:text-white"
        >
          Zum Forum
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-[160px] animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900"
              />
            ))
          : topics.map((topic) => (
              <ForumCard
                key={topic.id || topic._id}
                topic={topic}
                onClick={() => topic.slug && navigate(`/forum/topic/${topic.slug}`)}
              />
            ))}
      </div>
    </section>
  );
};

const ForumCard: React.FC<{ topic: ForumTopic; onClick: () => void }> = ({ topic, onClick }) => {
  const [avatarError, setAvatarError] = useState(false);
  const excerpt = toPlainText(
    topic.contentPlain && topic.contentPlain.trim() ? topic.contentPlain : topic.content,
  );
  const avatarUrl = topic.authorAvatarUrl || null;
  const showAvatar = avatarUrl && !avatarError;
  const initial = (topic.authorName || '?').charAt(0).toUpperCase();

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!topic.slug}
      className="card-lift flex h-full flex-col rounded-2xl border border-zinc-800 bg-zinc-900 p-5 text-left disabled:cursor-default"
    >
      {topic.categoryName && (
        <span className="mb-2 inline-flex w-fit items-center gap-1 rounded-md bg-zinc-800 px-2 py-0.5 text-[11px] font-medium text-gray-300">
          {topic.categoryName}
        </span>
      )}

      <h3 className="line-clamp-2 text-base font-bold text-white">{topic.title || 'Ohne Titel'}</h3>

      {excerpt && (
        <p className="mt-2 line-clamp-2 flex-1 text-sm text-gray-400">{excerpt}</p>
      )}

      <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
        {showAvatar ? (
          <img
            src={avatarUrl as string}
            alt=""
            onError={() => setAvatarError(true)}
            className="h-6 w-6 rounded-full object-cover"
          />
        ) : (
          <span
            className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-black"
            style={{ backgroundColor: ACCENT }}
          >
            {initial}
          </span>
        )}
        <span className="truncate font-medium text-gray-400">{topic.authorName || 'Unbekannt'}</span>
        <span className="ml-auto flex items-center gap-3">
          <span className="flex items-center gap-1">
            <ArrowBigUp size={14} /> {topic.score ?? 0}
          </span>
          <span className="flex items-center gap-1">
            <MessageSquare size={13} /> {topic.postCount ?? 0}
          </span>
        </span>
      </div>

      {topic.createdAt ? (
        <div className="mt-2 flex items-center gap-1 text-[11px] text-gray-600">
          <Clock size={11} /> {communityService.formatRelativeTime(topic.createdAt)}
        </div>
      ) : null}
    </button>
  );
};

export default ForumTopicsSection;
